import React, { memo, useCallback, useMemo } from 'react'

import { TextInput, Slider } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, number, string, func, oneOfType } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'

function PolyCountSlider({
  isDisabled = false,
  targetPolycount = 0,
  setTargetPolycount = NOOP,
  minimum = 0,
  maximum = 0,
}) {
  // Triangle's Slider emits an onChange of its own whenever min/max change under it.
  // Navigating into /ai from a model page clears the schema first, so the slider is
  // briefly rendered with a degenerate 0..0 range and that echo would overwrite a
  // valid target with 0 - leaving the input blank once the real range arrives. A drag
  // can never leave the native range input's own bounds, so anything outside them is
  // the echo, not the user.
  const handleChange = useCallback((value) => {
    if (maximum <= minimum || value < minimum || value > maximum) {
      return
    }

    setTargetPolycount(value)
  }, [minimum, maximum, setTargetPolycount])

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value
      if (value === '') {
        setTargetPolycount(value)

        return
      }

      const numValue = parseInt(value, 10)
      if (!Number.isNaN(numValue) && numValue >= 0) {
        setTargetPolycount(numValue)
      }
    },
    []
  )

  const handleInputBlur = useCallback(() => {
    if (targetPolycount < minimum) {
      setTargetPolycount(minimum)
    }

    if (targetPolycount > maximum) {
      setTargetPolycount(maximum)
    }
  }, [targetPolycount, minimum, maximum])

  const sliderValue = useMemo(() => {
    if (!targetPolycount || targetPolycount < minimum) {
      return minimum
    }

    if (targetPolycount > maximum) {
      return maximum
    }

    return targetPolycount
  }, [targetPolycount, minimum, maximum])

  return (
    <div className="model-settings__row model-settings__row--polycount-slider">
      {/*
        Triangle's Slider keeps its thumb position in internal state and only
        recomputes it against the min/max of the render that moved it. Mounted on
        the empty 0..0 range it strands the thumb at the far left, so remount it
        once the schema-driven range resolves.
      */}
      <Slider
        key={`${minimum}-${maximum}`}
        hideRangeValues
        max={maximum}
        min={minimum}
        step={500}
        onChange={handleChange}
        value={sliderValue}
        isDisabled={isDisabled}
        wrapperClassName="model-settings__polycount-slider"
      />
      <TextInput
        value={targetPolycount || ''}
        onChange={handleInputChange}
        size="small"
        id="custom-poly-count-input"
        isPilot
        textInputClassName="model-settings__polycount-input"
        onBlur={handleInputBlur}
        isDisabled={isDisabled}
      />
    </div>
  )
}

PolyCountSlider.propTypes = {
  isDisabled: bool,
  targetPolycount: oneOfType([number, string]),
  setTargetPolycount: func,
  minimum: number,
  maximum: number,
}

export default memo(PolyCountSlider)
